import { sectionRenderer } from '@theme/section-renderer';
import {
  mediaQueryLarge,
  productsMatchingTitle,
  requestIdleCallback,
  scoreProductTitle,
  startViewTransition,
} from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/** @type {Set<string>} */
const titleMatchQueries = new Set();

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #searchFallbackStarted = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#ensureSoldOutHidden()) return;

    this.#rankVisibleProducts();
    this.#syncTitleMatches();
  }

  /**
   * Shopify search misses some short names. Fill the page with every title that
   * contains the typed letters, closest match first.
   */
  async #syncTitleMatches() {
    const currentUrl = new URL(window.location.href);
    for (const key of currentUrl.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        this.#clearSearchLoadingState();
        return;
      }
    }

    const query = currentUrl.searchParams.get('q')?.trim() || '';
    if (!query || query.includes('id:') || query.includes(' OR ')) {
      this.#runSearchFallback();
      return;
    }

    const sort = currentUrl.searchParams.get('sort_by');
    if (sort && sort !== 'relevance') return;

    const shortOrEmpty = query.length <= 3 || Boolean(this.dataset.searchFallbackQuery);
    if (!shortOrEmpty) return;

    const queryKey = query.toLowerCase();
    if (titleMatchQueries.has(queryKey)) {
      this.#clearSearchLoadingState();
      return;
    }

    try {
      let matches = await productsMatchingTitle(query);
      if (Theme.settings?.hideSoldOutProducts) {
        matches = matches.filter((product) => product.available);
      }

      const gridIds = new Set(
        [...this.querySelectorAll('[data-product-id]')].map((element) => element.getAttribute('data-product-id'))
      );
      const missing = matches.some((product) => !gridIds.has(String(product.id)));
      if (!missing) {
        this.#clearSearchLoadingState();
        return;
      }

      const sectionId = this.getAttribute('section-id');
      if (!sectionId || matches.length === 0) {
        this.#runSearchFallback();
        return;
      }

      titleMatchQueries.add(queryKey);
      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      searchUrl.searchParams.set(
        'q',
        matches
          .slice(0, 24)
          .map((product) => `id:${product.id}`)
          .join(' OR ')
      );
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[prefix]', 'last');
      if (Theme.settings?.hideSoldOutProducts) {
        searchUrl.searchParams.set('options[unavailable_products]', 'hide');
      }

      this.setAttribute('data-search-loading', '');
      this.classList.add('search-results--loading');
      await sectionRenderer.renderSection(sectionId, { url: searchUrl, cache: false });
    } catch (error) {
      console.error(error);
      this.#clearSearchLoadingState();
      this.#runSearchFallback();
    }
  }

  /**
   * Search & Discovery and the theme setting exclude sold out products only when
   * the request includes options[unavailable_products]=hide (search) or
   * filter.v.availability=1 (collections with an Availability filter).
   * @returns {boolean} True when a section re-render was started.
   */
  #ensureSoldOutHidden() {
    if (this.dataset.hideSoldOut !== 'true') return false;

    const url = new URL(window.location.href);
    const sectionId = this.getAttribute('section-id');
    const isSearch = Boolean(url.searchParams.get('q')?.trim()) || this.dataset.pageType === 'search';
    const isCollection = this.dataset.pageType === 'collection' || url.pathname.includes('/collections/');

    if (isSearch && url.searchParams.get('q')?.trim()) {
      if (url.searchParams.get('options[unavailable_products]') === 'hide') {
        this.#removeSoldOutCards();
        return false;
      }

      url.searchParams.set('options[unavailable_products]', 'hide');
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== nextUrl) {
        history.replaceState(history.state, '', nextUrl);
      }

      if (!sectionId) return false;

      this.setAttribute('data-search-loading', '');
      this.classList.add('search-results--loading');
      sectionRenderer.renderSection(sectionId, { url, cache: false });
      return true;
    }

    if (isCollection) {
      const hasAvailabilityFilter = Boolean(document.querySelector('[name="filter.v.availability"]'));
      if (hasAvailabilityFilter && url.searchParams.get('filter.v.availability') !== '1') {
        url.searchParams.set('filter.v.availability', '1');
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== nextUrl) {
          history.replaceState(history.state, '', nextUrl);
        }
        if (!sectionId) {
          this.#removeSoldOutCards();
          return false;
        }
        sectionRenderer.renderSection(sectionId, { url, cache: false });
        return true;
      }
    }

    this.#removeSoldOutCards();
    return false;
  }

  /** Removes any sold-out product cards still present in the grid. */
  #removeSoldOutCards() {
    const grid = this.querySelector('[ref="grid"]') || this;
    for (const item of grid.querySelectorAll('[data-available="false"]')) {
      item.remove();
    }
  }

  /**
   * Puts the closest product titles first when the shopper has not chosen another sort.
   */
  #rankVisibleProducts() {
    const params = new URL(window.location.href).searchParams;
    const query = params.get('q')?.trim();
    if (!query || query.includes(' OR ') || query.includes('id:')) return;

    const sort = params.get('sort_by');
    if (sort && sort !== 'relevance') return;

    const grid = this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    if (this.dataset.hideSoldOut === 'true') {
      this.#removeSoldOutCards();
    }

    const items = [...grid.querySelectorAll(':scope > [data-product-id]')].filter(
      (item) => item instanceof HTMLElement
    );
    if (items.length < 2) return;

    const ranked = items
      .map((item, index) => ({
        item,
        index,
        score: scoreProductTitle(item.getAttribute('data-product-title') || '', query),
      }))
      .sort((a, b) => b.score - a.score || a.index - b.index);

    const orderChanged = ranked.some((entry, index) => entry.item !== items[index]);
    if (!orderChanged) return;

    for (const entry of ranked) {
      grid.append(entry.item);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    super.disconnectedCallback();
  }

  /**
   * When storefront search returns no products, load the same matches predictive search found.
   */
  async #runSearchFallback() {
    const query = this.dataset.searchFallbackQuery?.trim();
    if (!query || this.#searchFallbackStarted) return;

    // Filtered searches that correctly return 0 products must not be replaced
    // by predictive matches (which would drop the active filter params).
    const currentUrl = new URL(window.location.href);
    for (const key of currentUrl.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        this.#clearSearchLoadingState();
        delete this.dataset.searchFallbackQuery;
        return;
      }
    }

    this.#searchFallbackStarted = true;
    this.setAttribute('data-search-loading', '');
    this.classList.add('search-results--loading');

    try {
      const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
      predictiveUrl.searchParams.set('q', query);
      predictiveUrl.searchParams.set('resources[type]', 'product');
      predictiveUrl.searchParams.set('resources[limit]', '10');
      predictiveUrl.searchParams.set('resources[limit_scope]', 'each');
      predictiveUrl.searchParams.set('options[prefix]', 'last');
      if (Theme.settings?.hideSoldOutProducts) {
        predictiveUrl.searchParams.set('resources[options][unavailable_products]', 'hide');
      }

      const predictiveHtml = await sectionRenderer.getSectionHTML('predictive-search', false, predictiveUrl);
      const doc = new DOMParser().parseFromString(predictiveHtml, 'text/html');
      const productRows = [...doc.querySelectorAll('.predictive-search-results__card--product[data-product-id]')];
      const titlePhrases = productRows
        .map((row) => {
          const title = row.querySelector('.predictive-search-results__product-row-title')?.textContent?.trim();
          if (!title) return '';
          // First words as a quoted phrase — precise enough for matches, reliable for facets.
          // (id: OR queries often return products with empty search.filters.)
          return title
            .split(/\s+/)
            .slice(0, 5)
            .join(' ')
            .replace(/"/g, '');
        })
        .filter(Boolean);

      const uniquePhrases = [...new Set(titlePhrases)].slice(0, 24);
      const productIds = productRows
        .map((row) => row.getAttribute('data-product-id'))
        .filter((id) => id != null && id !== '');
      const uniqueIds = [...new Set(productIds)].slice(0, 24);

      if (uniquePhrases.length === 0 && uniqueIds.length === 0) {
        this.#showSearchFallbackEmpty();
        return;
      }

      const sectionId = this.getAttribute('section-id');
      if (!sectionId) return;

      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      if (uniquePhrases.length > 0) {
        searchUrl.searchParams.set('q', uniquePhrases.map((phrase) => `"${phrase}"`).join(' OR '));
      } else {
        searchUrl.searchParams.set('q', uniqueIds.map((id) => `id:${id}`).join(' OR '));
      }
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[prefix]', 'last');
      if (Theme.settings?.hideSoldOutProducts) {
        searchUrl.searchParams.set('options[unavailable_products]', 'hide');
      }

      await sectionRenderer.renderSection(sectionId, { url: searchUrl, cache: false });

      if (!this.querySelector('[ref="grid"] [ref="cards[]"], [ref="grid"] [data-product-id]')) {
        this.#showSearchFallbackEmpty();
      }
    } catch (error) {
      console.error(error);
      this.#showSearchFallbackEmpty();
    } finally {
      this.#clearSearchLoadingState();
      delete this.dataset.searchFallbackQuery;
    }
  }

  /**
   * Reveal the empty state after the predictive-search fallback finds no products.
   */
  #showSearchFallbackEmpty() {
    this.#clearSearchLoadingState();

    const loading = this.querySelector('[data-search-fallback-loading]');
    const existingEmpty = this.querySelector('[data-search-fallback-empty]');

    if (loading instanceof HTMLElement) {
      loading.hidden = true;
    }

    if (existingEmpty instanceof HTMLElement) {
      existingEmpty.hidden = false;
      return;
    }

    const template = this.querySelector('[data-search-fallback-empty-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const empty = template.content.firstElementChild;
    if (!(empty instanceof HTMLElement)) return;

    template.insertAdjacentElement('afterend', empty);
  }

  #clearSearchLoadingState() {
    this.removeAttribute('data-search-loading');
    this.classList.remove('search-results--loading');

    const loading = this.querySelector('[data-search-fallback-loading]');
    if (loading instanceof HTMLElement) {
      loading.hidden = true;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
